/**
 * Session and venue context.
 *
 * A user may hold different roles at different venues, so "which venue am I
 * looking at" is session state, not a URL parameter that every screen has to
 * thread. Permission checks are always scoped to the ACTIVE venue - a manager
 * at West Bay who is a host at The Pearl must see the host's screen at The
 * Pearl (D-043).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { activeVenue, api, setSignedOutHandler, tokens, type SessionUser } from './api.js';

/**
 * The permission matrix, mirrored from the API.
 *
 * The server is the authority and re-checks everything; this exists only so the
 * UI can hide what a role cannot do. A host who can see a disabled "Merge"
 * button learns the feature exists and asks for it - which is worse than the
 * button not being there.
 */
const ROLE_PERMISSIONS: Record<string, string[]> = {
  owner: ['*'],
  group_admin: ['*'],
  venue_manager: [
    'reservation:read', 'reservation:create', 'reservation:update', 'reservation:cancel',
    'reservation:seat', 'reservation:move', 'reservation:no_show',
    'waitlist:read', 'waitlist:manage',
    'guest:read', 'guest:read_contact', 'guest:update', 'guest:merge', 'guest:export',
    'floor:read', 'floor:configure', 'shift:configure',
    'report:read', 'report:financial', 'message:read', 'message:send',
    'user:read', 'audit:read',
  ],
  host: [
    'reservation:read', 'reservation:create', 'reservation:update', 'reservation:cancel',
    'reservation:seat', 'reservation:move', 'reservation:no_show',
    'waitlist:read', 'waitlist:manage', 'guest:read', 'floor:read', 'message:read',
  ],
  server: ['reservation:read', 'reservation:seat', 'floor:read', 'guest:read'],
  marketing: ['guest:read', 'guest:read_contact', 'guest:export', 'report:read', 'message:read'],
  call_centre: [
    'reservation:read', 'reservation:create', 'reservation:update', 'reservation:cancel',
    'waitlist:read', 'waitlist:manage', 'guest:read', 'guest:read_contact', 'guest:update',
    'message:read', 'message:send',
  ],
};

interface SessionValue {
  user: SessionUser | null;
  venueId: string | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  setVenue: (venueId: string) => void;
  /** Does the signed-in user hold this permission at the ACTIVE venue? */
  can: (permission: string) => boolean;
  role: string | null;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [venueId, setVenueId] = useState<string | null>(activeVenue.get());
  const [loading, setLoading] = useState(true);

  const signOut = useCallback(() => {
    const refresh = tokens.refresh();
    tokens.clear();
    setUser(null);
    // Best effort: the local tokens are already gone, so a failed request here
    // does not leave the host signed in.
    if (refresh) void api.post('/api/v1/auth/logout', { refresh_token: refresh }).catch(() => undefined);
  }, []);

  useEffect(() => {
    setSignedOutHandler(() => {
      tokens.clear();
      setUser(null);
    });
  }, []);

  // Restore a session on load, so a refreshed tab does not bounce a host to the
  // login screen mid-service.
  useEffect(() => {
    if (!tokens.access()) {
      setLoading(false);
      return;
    }
    api
      .get<SessionUser>('/api/v1/auth/me')
      .then((me) => {
        setUser(me);
        setVenueId((current) => current ?? me.venues[0]?.venueId ?? null);
      })
      .catch(() => tokens.clear())
      .finally(() => setLoading(false));
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await api.post<{
      access_token: string;
      refresh_token: string;
      user: SessionUser;
    }>('/api/v1/auth/login', { email, password });

    tokens.set(result.access_token, result.refresh_token);
    setUser(result.user);

    const firstVenue = result.user.venues[0]?.venueId ?? null;
    setVenueId((current) => {
      const keep = current && result.user.venues.some((v) => v.venueId === current) ? current : firstVenue;
      activeVenue.set(keep);
      return keep;
    });
  }, []);

  const setVenue = useCallback((next: string) => {
    activeVenue.set(next);
    setVenueId(next);
  }, []);

  const role = useMemo(
    () => user?.venues.find((v) => v.venueId === venueId)?.role ?? null,
    [user, venueId],
  );

  const can = useCallback(
    (permission: string) => {
      if (!role) return false;
      const granted = ROLE_PERMISSIONS[role] ?? [];
      return granted.includes('*') || granted.includes(permission);
    },
    [role],
  );

  const value = useMemo(
    () => ({ user, venueId, loading, signIn, signOut, setVenue, can, role }),
    [user, venueId, loading, signIn, signOut, setVenue, can, role],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside a SessionProvider');
  return context;
}

/** The active venue, asserted. Screens behind the auth gate always have one. */
export function useVenueId(): string {
  const { venueId } = useSession();
  if (!venueId) throw new Error('No active venue');
  return venueId;
}
